import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatasetStore } from '@/store/dataset-store';
import { useUiStore } from '@/store/ui-store';
import { ValueGroupingsModal } from './ValueGroupingsModal';

/**
 * Sidebar entry point that opens {@link ValueGroupingsModal} for the
 * active dataset. Shows a badge with the count of columns that have
 * at least one grouping defined.
 */
export function ValueGroupingsButton(): JSX.Element | null {
  const { t } = useTranslation();
  const activeId = useDatasetStore((s) => s.activeDatasetId);
  const datasets = useDatasetStore((s) => s.datasets);
  const open = useUiStore((s) => s.activeModal === 'value-groupings');
  const setActiveModal = useUiStore((s) => s.setActiveModal);

  const entry = useMemo(
    () => (activeId ? datasets.get(activeId) : undefined),
    [activeId, datasets],
  );

  if (!entry) return null;

  const count = entry.valueGroupings
    ? Object.keys(entry.valueGroupings).length
    : 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setActiveModal('value-groupings')}
        className="w-full text-xs px-2.5 py-2 rounded-md hover:opacity-80 text-left flex items-center justify-between gap-2"
        style={{
          color: 'var(--color-fg)',
          background: 'var(--color-hover)',
          border: '1px solid var(--color-border)',
        }}
      >
        <span>{t('sidebar.button.valueGroupings')}</span>
        {count > 0 && (
          <span
            className="text-[10px] px-1.5 rounded font-semibold"
            style={{
              background: 'var(--color-accent-subtle)',
              color: 'var(--color-accent)',
            }}
          >
            {count}
          </span>
        )}
      </button>
      {open && (
        <ValueGroupingsModal
          datasetId={entry.id}
          columns={entry.columns}
          current={entry.valueGroupings ?? {}}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  );
}
